import React, { useState, useRef } from 'react';
import { FileSpreadsheet, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { useCollaborations } from '../context/CollaborationsContext';
import { formatEuro } from '../utils/helpers';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { cn } from '../lib/utils';

const PROCESSED_KEY = 'influs_processed_orders';

function getProcessedOrders() {
  try {
    const raw = localStorage.getItem(PROCESSED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveProcessedOrders(set) {
  localStorage.setItem(PROCESSED_KEY, JSON.stringify([...set]));
}

export default function ImportModal({ onClose }) {
  const { collaborations, updateCollaboration } = useCollaborations();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  function processFile(file) {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Formato no válido. Usa .xlsx, .xls o .csv');
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });

        const dataRows =
          typeof rows[0]?.[0] === 'string' && isNaN(rows[0]?.[0]) ? rows.slice(1) : rows;

        const processedOrders = getProcessedOrders();
        const newProcessed = new Set(processedOrders);

        const collabMap = {};
        collaborations.forEach((c) => {
          if (c.codigoDescuento) {
            collabMap[c.codigoDescuento.toUpperCase()] = { collab: c, add: 0, orders: [] };
          }
        });

        let skippedDup = 0;
        let skippedUnknown = 0;
        let matched = 0;

        dataRows.forEach((row) => {
          const pedido = String(row[0] ?? '').trim();
          const importe = parseFloat(row[1]) || 0;
          const cupon = String(row[2] ?? '').trim().toUpperCase();

          if (!pedido || !cupon) return;
          if (processedOrders.has(pedido)) {
            skippedDup++;
            return;
          }

          if (collabMap[cupon]) {
            collabMap[cupon].add += importe;
            collabMap[cupon].orders.push(pedido);
            newProcessed.add(pedido);
            matched++;
          } else {
            skippedUnknown++;
          }
        });

        const updated = [];
        Object.values(collabMap).forEach(({ collab, add, orders }) => {
          if (orders.length > 0) {
            const newIncome = (Number(collab.income) || 0) + add;
            updateCollaboration(collab.id, { income: newIncome });
            updated.push({
              name: collab.name,
              codigo: collab.codigoDescuento,
              add,
              orders: orders.length,
            });
          }
        });

        saveProcessedOrders(newProcessed);
        setResult({ updated, skippedDup, skippedUnknown, matched });
        setLoading(false);

        if (updated.length > 0) {
          toast.success(`Ingresos actualizados para ${updated.length} influencer(s)`);
        } else {
          toast.info('No se encontraron ventas nuevas');
        }
      } catch (err) {
        setLoading(false);
        toast.error('Error al leer el archivo');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  function handleFile(e) {
    processFile(e.target.files[0]);
  }

  function handleReset() {
    if (!window.confirm('¿Quieres borrar todos los ingresos?')) return;
    collaborations.forEach((c) => {
      if ((Number(c.income) || 0) !== 0) {
        updateCollaboration(c.id, { income: 0 });
      }
    });
    localStorage.removeItem(PROCESSED_KEY);
    setResult(null);
    toast.success('Ingresos y pedidos importados borrados');
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files[0]);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>Importar ventas</DialogTitle>
              <DialogDescription>
                Actualiza los ingresos desde una hoja de cálculo
              </DialogDescription>
            </div>
            {/* mr-8: la X de cerrar del diálogo va en absolute right-4, así que sin
                este hueco el botón se le metía debajo. */}
            <Button
              variant="outline"
              size="icon"
              onClick={handleReset}
              title="Resetear ingresos"
              className="shrink-0 mr-8"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={cn(
                'border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors flex flex-col items-center',
                dragging ? 'border-primary bg-accent' : 'border-border bg-muted/40 hover:bg-muted',
              )}
            >
              <FileSpreadsheet
                className={cn(
                  'h-9 w-9 mb-3',
                  dragging ? 'text-primary' : 'text-muted-foreground',
                )}
              />
              <p className="font-semibold text-sm">
                {loading ? 'Procesando...' : 'Arrastra tu archivo aquí'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                o haz clic para seleccionarlo
              </p>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="file-upload-input"
              onChange={handleFile}
            />
            <div className="rounded-md bg-accent text-accent-foreground/80 p-3 text-xs leading-relaxed">
              <strong className="block mb-1 text-accent-foreground">Notas</strong>
              Solo válido para archivos Excel o CSV.
              <br />
              Los pedidos ya importados no se procesarán de nuevo.
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Ventas nuevas', value: result.matched, color: 'text-green-600' },
                { label: 'Ya importadas', value: result.skippedDup, color: 'text-muted-foreground' },
                { label: 'Sin influencer', value: result.skippedUnknown, color: 'text-amber-500' },
              ].map((s) => (
                <Card key={s.label}>
                  <CardContent className="p-3 text-center">
                    <div className={cn('text-2xl font-bold', s.color)}>{s.value}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{s.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {result.updated.length > 0 ? (
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Influencers actualizados
                </div>
                <div className="space-y-2">
                  {result.updated.map((u) => (
                    <Card key={u.name}>
                      <CardContent className="p-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold">{u.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {u.codigo} · {u.orders} {u.orders === 1 ? 'pedido' : 'pedidos'}
                          </div>
                        </div>
                        <div className="text-sm font-bold text-green-600">
                          + {formatEuro(u.add)}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-5 text-sm text-muted-foreground">
                No se encontraron ventas nuevas para ningún influencer
              </div>
            )}

            <Button variant="outline" onClick={() => setResult(null)} className="w-full">
              Importar otro archivo
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
